import type { EffectRef, Signal } from '@angular/core';
import { effect } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { isNil } from '@pct-ts/typeguard';

/**
 * Disables or enables an AbstractControl instance based on a loading signal flag.
 * Additionally, selectively disables specific controls by their paths if defined.
 *
 * @param {AbstractControl} abstractControl The AbstractControl instance to enable or disable.
 * @param {Signal<boolean>} loadingSignalFlag A signal flag indicating the loading state. The control is disabled when true and enabled when false.
 * @param {string[]} [disabledControlPaths=[]] Optional array of control paths to be disabled when the flag is active.
 * @return {EffectRef} The effect reference that tracks the state of the operation.
 */
export function disableAbstractControlWhileLoading(
  abstractControl: AbstractControl,
  loadingSignalFlag: Signal<boolean>,
  disabledControlPaths: string[] = [],
): EffectRef {
  return effect(() => {
    const isLoading = loadingSignalFlag();
    if (isLoading) {
      abstractControl.disable({ emitEvent: false });
      return;
    }
    abstractControl.enable({ emitEvent: false });
    if (disabledControlPaths.length === 0) {
      return;
    }
    disableSpecificControls();
  });

  function disableSpecificControls(): void {
    for (const path of disabledControlPaths) {
      const control = abstractControl.get(path);
      if (isNil(control)) {
        console.error(`[disableAbstractControlWhileLoading] Control not found: ${path}`);
        continue;
      }
      control.disable({ emitEvent: false });
    }
  }
}
